import { TOKENS } from "../core/palette";
import { layoutMessageBoard, type BoardText } from "./boardLayout";
import { SIGN_TEXTURE, layoutSlot, logoRect, placeLines, type MeasureFn, type Region, type SignAtlas, type SignLayout } from "./layout";

/**
 * The canvas half of the sign painter, with no Babylon dependency (ADR-0020): the business sign atlas (rooftop board,
 * storefront banner, interior sign) and the in-store message board are painted here onto any 2D canvas context. The
 * town's DynamicTextures (./signPainter.ts) and the store screen's live preview (ui/storeSetup) call the same functions,
 * so the preview is the town's own drawing. Canvas text only: owner-written text is never interpreted as markup.
 */
export type Ctx2D = CanvasRenderingContext2D;

export interface BusinessSignSpec {
  businessName: string;
  monogram: string;
  brandColor: string;
  accentColor: string;
  /** Draw a small house glyph instead of the monogram (civic Real Estate office, home wall labels). */
  houseGlyph?: boolean;
  /**
   * Civic shop glyphs: house + hammer (Home Builders) or an armchair (Furnishings); world v4 attractions: a
   * star (Townsy Casino), a horseshoe (Townsy Downs), a ball (Townsy Sports Center), a wheel (Monster Truck Arena);
   * ADR-0044, the Sports Park's stadiums: an American football, a soccer ball, a baseball, a basketball, a puck and sticks.
   */
  glyph?: "house" | "builder" | "armchair" | "star" | "horseshoe" | "ball" | "wheel" | "football" | "soccer" | "baseball" | "basketball" | "puck";
  /** The store's rooftop board is square (it has an approved logo URL): the stacked layout uses its centred square. */
  squareRooftop?: boolean;
  /** M5: the approved raster logo (decoded same-origin derivative) drawn in the tile instead of the monogram. */
  logo?: SignImage | null;
}

/** A decoded approved image ready to draw (media/mediaLoader.ts DecodedImage). */
export interface SignImage {
  readonly source: CanvasImageSource;
  readonly width: number;
  readonly height: number;
  /** Uniform opaque corner colour (the logo's own padding), used as the tile / letterbox fill; null = default. */
  readonly edgeColor: string | null;
}

export const FONT_STACK = '"Segoe UI", system-ui, Roboto, "Helvetica Neue", Arial, sans-serif';
export const EMOJI_STACK = '"Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji"';
export const MARKER_STACK = '"Segoe Print", "Bradley Hand", "Marker Felt", "Comic Neue", "Segoe UI", cursive';

export function font(px: number, weight = 700, emoji = false, family = FONT_STACK): string {
  return `${weight} ${Math.round(px)}px ${family}${emoji ? `, ${EMOJI_STACK}` : ""}`;
}

/**
 * Text measurement for the pure layouts. Assigning `ctx.font` parses a CSS font string, and the
 * contain-fit layouts measure hundreds of times while stepping the size down, so the font is only
 * re-assigned when the size changed or something else (the painter) changed `ctx.font` since: the
 * context's own serialisation is compared, so a stale font can never be used.
 */
export function makeMeasure(ctx: Ctx2D, weight = 700, emoji = false, family = FONT_STACK): MeasureFn {
  let lastPx = Number.NaN;
  let lastFont = "";
  return (text, px) => {
    if (px !== lastPx || ctx.font !== lastFont) {
      ctx.font = font(px, weight, emoji, family);
      lastPx = px;
      lastFont = ctx.font;
    }
    return ctx.measureText(text).width;
  };
}

/**
 * For layouts that try many sizes of large text (the billboards fit up to ~120 px, stepping 1 px):
 * each string is measured ONCE at `refPx` and scaled linearly, plus a small safety margin, so the
 * text drawn at the chosen size is never wider than the layout planned. Measuring at dozens of
 * distinct sizes made the first text-creative paint take ~50 ms (a visible hitch).
 */
export function makeScaledMeasure(ctx: Ctx2D, weight = 700, refPx = 100, safety = 1.015): MeasureFn {
  const exact = makeMeasure(ctx, weight);
  const widths = new Map<string, number>();
  return (text, px) => {
    let w = widths.get(text);
    if (w === undefined) {
      w = exact(text, refPx);
      widths.set(text, w);
    }
    return ((w * px) / refPx) * safety;
  };
}

export function roundedRect(ctx: Ctx2D, r: Region, radius: number): void {
  const rad = Math.min(radius, r.w / 2, r.h / 2);
  ctx.beginPath();
  ctx.moveTo(r.x + rad, r.y);
  ctx.lineTo(r.x + r.w - rad, r.y);
  ctx.arcTo(r.x + r.w, r.y, r.x + r.w, r.y + rad, rad);
  ctx.lineTo(r.x + r.w, r.y + r.h - rad);
  ctx.arcTo(r.x + r.w, r.y + r.h, r.x + r.w - rad, r.y + r.h, rad);
  ctx.lineTo(r.x + rad, r.y + r.h);
  ctx.arcTo(r.x, r.y + r.h, r.x, r.y + r.h - rad, rad);
  ctx.lineTo(r.x, r.y + rad);
  ctx.arcTo(r.x, r.y, r.x + rad, r.y, rad);
  ctx.closePath();
}

export function hexWithAlpha(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return hex;
  return `rgba(${parseInt(m[1]!, 16)}, ${parseInt(m[2]!, 16)}, ${parseInt(m[3]!, 16)}, ${alpha})`;
}

/** Euclidean RGB distance between two #RRGGBB colours (441 for black vs white; Infinity if unparsable). */
export function colorDistance(a: string, b: string): number {
  const pa = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(a);
  const pb = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(b);
  if (!pa || !pb) return Number.POSITIVE_INFINITY;
  let sum = 0;
  for (let i = 1; i <= 3; i++) sum += (parseInt(pa[i]!, 16) - parseInt(pb[i]!, 16)) ** 2;
  return Math.sqrt(sum);
}

/** Relative luminance to decide whether white or ink text reads better on a brand colour. */
export function readableTextOn(hex: string): string {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return "#FFFFFF";
  const lin = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const l = 0.2126 * lin(parseInt(m[1]!, 16)) + 0.7152 * lin(parseInt(m[2]!, 16)) + 0.0722 * lin(parseInt(m[3]!, 16));
  return l > 0.5 ? TOKENS.ink : "#FFFFFF";
}

/** A simple house outline (roof + body + door) centred in a tile: original vector glyph, no font. */
export function drawHouse(ctx: Ctx2D, tile: Region, color: string): void {
  const s = tile.w * 0.62;
  const x0 = tile.x + (tile.w - s) / 2;
  const y0 = tile.y + (tile.h - s) / 2;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x0 + s / 2, y0);
  ctx.lineTo(x0 + s, y0 + s * 0.46);
  ctx.lineTo(x0 + s * 0.86, y0 + s * 0.46);
  ctx.lineTo(x0 + s * 0.86, y0 + s);
  ctx.lineTo(x0 + s * 0.14, y0 + s);
  ctx.lineTo(x0 + s * 0.14, y0 + s * 0.46);
  ctx.lineTo(x0, y0 + s * 0.46);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(x0 + s * 0.42, y0 + s * 0.66, s * 0.16, s * 0.34);
}

/** House outline with a hammer across its lower right (Townsy Home Builders): original vector glyph. */
function drawBuilder(ctx: Ctx2D, tile: Region, color: string): void {
  const s = tile.w * 0.6;
  const x0 = tile.x + (tile.w - s) / 2 - s * 0.06;
  const y0 = tile.y + (tile.h - s) / 2;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x0 + s * 0.44, y0);
  ctx.lineTo(x0 + s * 0.88, y0 + s * 0.42);
  ctx.lineTo(x0 + s * 0.76, y0 + s * 0.42);
  ctx.lineTo(x0 + s * 0.76, y0 + s * 0.9);
  ctx.lineTo(x0 + s * 0.12, y0 + s * 0.9);
  ctx.lineTo(x0 + s * 0.12, y0 + s * 0.42);
  ctx.lineTo(x0, y0 + s * 0.42);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(x0 + s * 0.34, y0 + s * 0.6, s * 0.18, s * 0.3);
  // Hammer: handle + head, rotated 40 degrees, overlapping the house's right corner.
  ctx.save();
  ctx.translate(x0 + s * 0.86, y0 + s * 0.74);
  ctx.rotate(-0.7);
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(-s * 0.075, -s * 0.06, s * 0.15, s * 0.62);
  ctx.fillStyle = TOKENS.ink;
  ctx.fillRect(-s * 0.045, -s * 0.03, s * 0.09, s * 0.56);
  ctx.fillRect(-s * 0.2, -s * 0.13, s * 0.4, s * 0.15);
  ctx.restore();
}

/** Armchair silhouette (Townsy Furnishings): original vector glyph. */
function drawArmchair(ctx: Ctx2D, tile: Region, color: string): void {
  const s = tile.w * 0.62;
  const x0 = tile.x + (tile.w - s) / 2;
  const y0 = tile.y + (tile.h - s) / 2;
  ctx.fillStyle = color;
  const r = (x: number, y: number, w: number, h: number, rad: number) => {
    roundedRect(ctx, { x: x0 + x * s, y: y0 + y * s, w: w * s, h: h * s }, rad * s);
    ctx.fill();
  };
  r(0.16, 0.06, 0.68, 0.5, 0.1); // back
  r(0.0, 0.36, 0.22, 0.42, 0.08); // left arm
  r(0.78, 0.36, 0.22, 0.42, 0.08); // right arm
  r(0.14, 0.52, 0.72, 0.24, 0.05); // seat
  ctx.fillRect(x0 + s * 0.08, y0 + s * 0.78, s * 0.08, s * 0.16);
  ctx.fillRect(x0 + s * 0.84, y0 + s * 0.78, s * 0.08, s * 0.16);
  ctx.fillStyle = "rgba(255,255,255,0.35)";
  ctx.fillRect(x0 + s * 0.2, y0 + s * 0.56, s * 0.6, s * 0.04);
}

/** Five-pointed star (Townsy Casino): original vector glyph. */
function drawStar(ctx: Ctx2D, tile: Region, color: string): void {
  const cx = tile.x + tile.w / 2;
  const cy = tile.y + tile.h / 2 + tile.h * 0.03;
  const outer = tile.w * 0.36;
  const inner = outer * 0.42;
  ctx.fillStyle = color;
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    if (i === 0) ctx.moveTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
    else ctx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
  }
  ctx.closePath();
  ctx.fill();
}

/** Horseshoe, open end up, with nail holes (Townsy Downs): original vector glyph. */
function drawHorseshoe(ctx: Ctx2D, tile: Region, color: string): void {
  const cx = tile.x + tile.w / 2;
  const cy = tile.y + tile.h * 0.46;
  const r = tile.w * 0.27;
  const t = tile.w * 0.12;
  ctx.strokeStyle = color;
  ctx.lineWidth = t;
  ctx.lineCap = "butt";
  ctx.beginPath();
  ctx.arc(cx, cy, r, Math.PI * 0.05, Math.PI * 0.95, false);
  ctx.lineTo(cx - r * Math.cos(Math.PI * 0.05), cy - r * 0.55);
  ctx.moveTo(cx + r * Math.cos(Math.PI * 0.05), cy + r * Math.sin(Math.PI * 0.05));
  ctx.lineTo(cx + r * Math.cos(Math.PI * 0.05), cy - r * 0.55);
  ctx.stroke();
  ctx.fillStyle = "#FFFFFF";
  for (let i = 0; i < 6; i++) {
    const a = Math.PI * (0.18 + (i * 0.64) / 5);
    ctx.beginPath();
    ctx.arc(cx + Math.cos(a) * r, cy + Math.sin(a) * r, t * 0.16, 0, Math.PI * 2);
    ctx.fill();
  }
}

/** A ball with seams (Townsy Sports Center): original vector glyph. */
function drawBall(ctx: Ctx2D, tile: Region, color: string): void {
  const cx = tile.x + tile.w / 2;
  const cy = tile.y + tile.h / 2;
  const r = tile.w * 0.33;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#FFFFFF";
  ctx.lineWidth = Math.max(2, r * 0.09);
  ctx.beginPath();
  ctx.moveTo(cx - r, cy);
  ctx.lineTo(cx + r, cy);
  ctx.moveTo(cx, cy - r);
  ctx.lineTo(cx, cy + r);
  ctx.stroke();
  // Two curved seams, bulging toward the centre.
  ctx.beginPath();
  ctx.arc(cx - r * 1.35, cy, r, -0.82, 0.82, false);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx + r * 1.35, cy, r, Math.PI - 0.82, Math.PI + 0.82, false);
  ctx.stroke();
}

/** A big tyre with treads and a hub (Monster Truck Arena): original vector glyph. */
function drawWheel(ctx: Ctx2D, tile: Region, color: string): void {
  const cx = tile.x + tile.w / 2;
  const cy = tile.y + tile.h / 2;
  const r = tile.w * 0.36;
  ctx.fillStyle = color;
  for (let i = 0; i < 12; i++) {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate((i * Math.PI) / 6);
    ctx.fillRect(-r * 0.16, -r * 1.06, r * 0.32, r * 0.2);
    ctx.restore();
  }
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.92, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#FFFFFF";
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.48, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.2, 0, Math.PI * 2);
  ctx.fill();
  for (let i = 0; i < 5; i++) {
    const a = -Math.PI / 2 + (i * Math.PI * 2) / 5;
    ctx.beginPath();
    ctx.arc(cx + Math.cos(a) * r * 0.34, cy + Math.sin(a) * r * 0.34, r * 0.06, 0, Math.PI * 2);
    ctx.fill();
  }
}

/** An American football with laces (Titans Field): original vector glyph. */
function drawFootball(ctx: Ctx2D, tile: Region, color: string): void {
  const cx = tile.x + tile.w / 2;
  const cy = tile.y + tile.h / 2;
  const rx = tile.w * 0.38;
  const ry = tile.w * 0.22;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(-Math.PI / 6);
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#FFFFFF";
  ctx.lineWidth = Math.max(2, ry * 0.12);
  ctx.beginPath();
  ctx.moveTo(-rx * 0.38, 0);
  ctx.lineTo(rx * 0.38, 0);
  for (let i = -2; i <= 2; i++) {
    ctx.moveTo((i * rx * 0.36) / 2, -ry * 0.22);
    ctx.lineTo((i * rx * 0.36) / 2, ry * 0.22);
  }
  // White bands near both tips.
  for (const s of [-1, 1]) {
    ctx.moveTo(s * rx * 0.72, -ry * 0.62);
    ctx.lineTo(s * rx * 0.72, ry * 0.62);
  }
  ctx.stroke();
  ctx.restore();
}

/** A soccer ball: a pentagon in the middle and five more round it (Townsy FC Park): original vector glyph. */
function drawSoccer(ctx: Ctx2D, tile: Region, color: string): void {
  const cx = tile.x + tile.w / 2;
  const cy = tile.y + tile.h / 2;
  const r = tile.w * 0.34;
  ctx.fillStyle = "#FFFFFF";
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(2, r * 0.08);
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  const pentagon = (x: number, y: number, s: number, rot: number) => {
    ctx.beginPath();
    for (let i = 0; i < 5; i++) {
      const a = rot + (i * Math.PI * 2) / 5;
      if (i === 0) ctx.moveTo(x + Math.cos(a) * s, y + Math.sin(a) * s);
      else ctx.lineTo(x + Math.cos(a) * s, y + Math.sin(a) * s);
    }
    ctx.closePath();
    ctx.fill();
  };
  ctx.fillStyle = color;
  pentagon(cx, cy, r * 0.3, -Math.PI / 2);
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.clip();
  for (let i = 0; i < 5; i++) {
    const a = -Math.PI / 2 + Math.PI / 5 + (i * Math.PI * 2) / 5;
    pentagon(cx + Math.cos(a) * r * 0.92, cy + Math.sin(a) * r * 0.92, r * 0.26, a + Math.PI / 5);
  }
  ctx.restore();
}

/** A baseball with its two curved red-stitched seams (Sluggers Ballpark): original vector glyph. */
function drawBaseball(ctx: Ctx2D, tile: Region, color: string): void {
  const cx = tile.x + tile.w / 2;
  const cy = tile.y + tile.h / 2;
  const r = tile.w * 0.34;
  ctx.fillStyle = "#FFFFFF";
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(2, r * 0.08);
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.lineWidth = Math.max(2, r * 0.07);
  for (const s of [-1, 1]) {
    ctx.beginPath();
    ctx.arc(cx + s * r * 1.32, cy, r * 0.95, s > 0 ? Math.PI - 0.78 : -0.78, s > 0 ? Math.PI + 0.78 : 0.78, false);
    ctx.stroke();
    // Stitches across each seam.
    for (let k = -3; k <= 3; k++) {
      const a = (s > 0 ? Math.PI : 0) + k * 0.2;
      const px = cx + s * r * 1.32 + Math.cos(a) * r * 0.95;
      const py = cy + Math.sin(a) * r * 0.95;
      ctx.beginPath();
      ctx.moveTo(px - s * r * 0.08, py - r * 0.05);
      ctx.lineTo(px + s * r * 0.08, py + r * 0.05);
      ctx.stroke();
    }
  }
}

/** A basketball: a cross and two side seams (Hoopers Arena): original vector glyph. */
function drawBasketball(ctx: Ctx2D, tile: Region, color: string): void {
  const cx = tile.x + tile.w / 2;
  const cy = tile.y + tile.h / 2;
  const r = tile.w * 0.34;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#FFFFFF";
  ctx.lineWidth = Math.max(2, r * 0.08);
  ctx.beginPath();
  ctx.moveTo(cx - r, cy);
  ctx.lineTo(cx + r, cy);
  ctx.moveTo(cx, cy - r);
  ctx.lineTo(cx, cy + r);
  ctx.stroke();
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.clip();
  for (const s of [-1, 1]) {
    ctx.beginPath();
    ctx.arc(cx + s * r * 1.15, cy, r * 0.8, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
}

/** Two crossed hockey sticks over a puck (Blizzard Ice Center): original vector glyph. */
function drawPuck(ctx: Ctx2D, tile: Region, color: string): void {
  const cx = tile.x + tile.w / 2;
  const cy = tile.y + tile.h / 2;
  const L = tile.w * 0.62;
  const t = tile.w * 0.07;
  ctx.fillStyle = color;
  for (const s of [-1, 1]) {
    ctx.save();
    ctx.translate(cx, cy - tile.h * 0.04);
    ctx.rotate(s * 0.62);
    ctx.fillRect(-t / 2, -L / 2, t, L);
    // The blade, turned out at the bottom.
    ctx.fillRect(s > 0 ? -t / 2 : -t * 2.6 + t / 2, L / 2 - t * 1.1, t * 2.6, t * 1.1);
    ctx.restore();
  }
  ctx.beginPath();
  ctx.ellipse(cx, cy + tile.h * 0.3, tile.w * 0.13, tile.w * 0.06, 0, 0, Math.PI * 2);
  ctx.fill();
}

export function paintLayout(ctx: Ctx2D, l: SignLayout, spec: BusinessSignSpec, textColor: string): void {
  // Brand background with a subtle inset border in the accent colour (inside the region's padding).
  ctx.fillStyle = spec.brandColor;
  ctx.fillRect(l.region.x, l.region.y, l.region.w, l.region.h);
  const inset = Math.max(3, Math.round(l.pad * 0.35));
  ctx.strokeStyle = hexWithAlpha(spec.accentColor, 0.55);
  ctx.lineWidth = Math.max(2, Math.round(Math.min(l.region.w, l.region.h) * 0.012));
  ctx.strokeRect(l.region.x + inset, l.region.y + inset, l.region.w - inset * 2, l.region.h - inset * 2);
  // White monogram tile (contain-fit). With an approved logo the tile takes the logo's own padding
  // colour (when its corners are one opaque colour) so the padded square sits flush in the tile; the
  // artwork itself is drawn unchanged, aspect preserved (docs/01 section 2).
  ctx.fillStyle = spec.logo?.edgeColor ?? "#FFFFFF";
  roundedRect(ctx, l.tile, Math.min(l.tile.w, l.tile.h) * 0.16);
  ctx.fill();
  if (spec.logo) {
    const r = logoRect(l.tile, spec.logo.width, spec.logo.height);
    ctx.save();
    roundedRect(ctx, l.tile, Math.min(l.tile.w, l.tile.h) * 0.16);
    ctx.clip();
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(spec.logo.source, r.x, r.y, r.w, r.h);
    ctx.restore();
    // A logo padded in (nearly) the board's own colour would melt into the board: a thin light
    // outline keeps the tile, and so the logo, reading as one plain surface.
    if (spec.logo.edgeColor && colorDistance(spec.logo.edgeColor, spec.brandColor) < 48) {
      ctx.strokeStyle = "rgba(255,255,255,0.6)";
      ctx.lineWidth = Math.max(2, Math.round(Math.min(l.tile.w, l.tile.h) * 0.025));
      roundedRect(ctx, l.tile, Math.min(l.tile.w, l.tile.h) * 0.16);
      ctx.stroke();
    }
  } else if (spec.glyph === "builder") {
    drawBuilder(ctx, l.tile, spec.brandColor);
  } else if (spec.glyph === "armchair") {
    drawArmchair(ctx, l.tile, spec.brandColor);
  } else if (spec.glyph === "star") {
    drawStar(ctx, l.tile, spec.brandColor);
  } else if (spec.glyph === "horseshoe") {
    drawHorseshoe(ctx, l.tile, spec.brandColor);
  } else if (spec.glyph === "ball") {
    drawBall(ctx, l.tile, spec.brandColor);
  } else if (spec.glyph === "wheel") {
    drawWheel(ctx, l.tile, spec.brandColor);
  } else if (spec.glyph === "football") {
    drawFootball(ctx, l.tile, spec.brandColor);
  } else if (spec.glyph === "soccer") {
    drawSoccer(ctx, l.tile, spec.brandColor);
  } else if (spec.glyph === "baseball") {
    drawBaseball(ctx, l.tile, spec.brandColor);
  } else if (spec.glyph === "basketball") {
    drawBasketball(ctx, l.tile, spec.accentColor);
  } else if (spec.glyph === "puck") {
    drawPuck(ctx, l.tile, spec.brandColor);
  } else if (spec.houseGlyph || spec.glyph === "house") {
    drawHouse(ctx, l.tile, spec.brandColor);
  } else {
    ctx.fillStyle = spec.brandColor;
    ctx.font = font(l.monogramPx, 800);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(spec.monogram, l.tile.x + l.tile.w / 2, l.tile.y + l.tile.h / 2 + l.monogramPx * 0.04);
  }
  // Business name, contain-fit (no maxWidth squeeze: the layout already guarantees the width).
  ctx.fillStyle = textColor;
  ctx.font = font(l.name.fontPx, 700);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  for (const line of placeLines(l.name, l.nameBox)) ctx.fillText(line.text, line.x, line.y);
}


/** Paint every atlas slot (rooftop board, banners, interior sign) for one business onto a canvas of SIGN_TEXTURE size. */
export function paintBusinessCanvas(ctx: Ctx2D, spec: BusinessSignSpec, atlas: SignAtlas): void {
  const measure = makeMeasure(ctx);
  const textColor = readableTextOn(spec.brandColor);
  ctx.clearRect(0, 0, SIGN_TEXTURE.width, SIGN_TEXTURE.height);
  // Unused atlas space takes the brand colour so bilinear bleed at region edges stays on-brand.
  ctx.fillStyle = spec.brandColor;
  ctx.fillRect(0, 0, SIGN_TEXTURE.width, SIGN_TEXTURE.height);
  const opts = { logo: Boolean(spec.logo), square: Boolean(spec.squareRooftop) };
  for (const slot of atlas.slots) paintLayout(ctx, layoutSlot(slot, spec.businessName, spec.monogram, measure, opts), spec, textColor);
}

/** Store marketing board onto a W x H canvas: dark TV screen or whiteboard with marker text; "No message yet" when empty. */
export function paintMessageBoardCanvas(ctx: Ctx2D, W: number, H: number, board: BoardText | null, style: "tv" | "whiteboard"): void {
  const family = style === "whiteboard" ? MARKER_STACK : FONT_STACK;
  const bold = makeMeasure(ctx, 700, false, family);
  const regular = makeMeasure(ctx, style === "whiteboard" ? 400 : 500, false, family);
  const l = layoutMessageBoard(board, bold, regular, W, H);
  if (style === "tv") {
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, "#1D2B4A");
    g.addColorStop(1, "#0F1626");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "rgba(255,255,255,0.06)";
    roundedRect(ctx, l.screen, 18);
    ctx.fill();
    ctx.fillStyle = TOKENS.amber;
    ctx.fillRect(l.screen.x + 6, l.titleBox.y + l.titleBox.h + 4, Math.min(160, l.screen.w * 0.2), 6);
  } else {
    ctx.fillStyle = "#FDFDFB";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "rgba(40,81,217,0.05)";
    ctx.fillRect(l.screen.x, l.screen.y, l.screen.w, l.screen.h);
  }
  const titleColor = style === "tv" ? (l.empty ? "#C9D3E6" : "#FFFFFF") : l.empty ? "#7A808C" : "#1F3FA8";
  const bodyColor = style === "tv" ? (l.empty ? "#8E9BB4" : "#DCE4F2") : l.empty ? "#8A8F99" : "#1B1F2A";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = titleColor;
  ctx.font = font(l.title.fontPx, 700, false, family);
  for (const line of l.titleLines) ctx.fillText(line.text, line.x, line.y);
  ctx.fillStyle = bodyColor;
  ctx.font = font(l.body.fontPx, style === "whiteboard" ? 400 : 500, false, family);
  for (const line of l.bodyLines) ctx.fillText(line.text, line.x, line.y);
}
